/* TEMPORARY TEST HARNESS: Remove before production release. */
import { Box } from "@mui/material";
import {
   AttentionBlock,
   Button,
   Paragraph,
} from "@nn-design-system/react-component-library";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomInput from "../../Components/CustomInputs/CustomInput";
import useFetch from "../../CustomHooks/useFetch";
import {
   login,
   setStelaContext,
   setUserAuthenticationStatus,
} from "../../redux/reducers/authReducer";
import { setToken } from "../../redux/reducers/tokenReducer";

type StelaInsuredPersonResponse = {
   firstname: string;
   lastname: string;
   personalId: string;
};

type StelaCallbackResponse = {
   jwtToken: string;
   proposalNumber: string;
   insuredPersons: StelaInsuredPersonResponse[];
   returnUrl: string;
   agentId?: string;
   agentFirstname?: string;
   agentLastname?: string;
   agentIco?: string;
   agentPhone?: string;
   agentEmail?: string;
};

type StelaTestEntryForm = {
   proposalNumber: string;
   agentId: string;
   agentFirstname: string;
   agentLastname: string;
   agentIco: string;
   agentPhone: string;
   agentEmail: string;
};

const initialForm: StelaTestEntryForm = {
   proposalNumber: "",
   agentId: "",
   agentFirstname: "",
   agentLastname: "",
   agentIco: "",
   agentPhone: "",
   agentEmail: "",
};

const StelaTestEntryPage: React.FC = () => {
   const [form, setForm] = useState<StelaTestEntryForm>(initialForm);
   const [formError, setFormError] = useState("");
   const [requestError, setRequestError] = useState("");
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const { sendRequest, data, error } = useFetch<StelaCallbackResponse>();

   useEffect(() => {
      if (!data) {
         return;
      }

      dispatch(setToken(data.jwtToken));
      dispatch(setStelaContext({
         proposalNumber: data.proposalNumber,
         insuredPersonBirthNumber: "",
         entryJwtToken: data.jwtToken,
         insuredPersons: data.insuredPersons.map((person) => ({
            ...person,
            isNotInsured: false,
         })),
         returnUrl: data.returnUrl,
         agentId: data.agentId ?? "",
         agentFirstname: data.agentFirstname ?? "",
         agentLastname: data.agentLastname ?? "",
         agentIco: data.agentIco ?? "",
         agentPhone: data.agentPhone ?? "",
         agentEmail: data.agentEmail ?? "",
      }));
      dispatch(login());
      dispatch(setUserAuthenticationStatus("agent"));
      navigate("/personSelection/stela");
   }, [data, dispatch, navigate]);

   useEffect(() => {
      if (!error) {
         return;
      }

      const response = error?.response;

      setRequestError(
         response?.status === 404
            ? "Testovací vstup není dostupný nebo smlouvu nelze najít."
            : "Nepodařilo se zahájit testovací vstup."
      );
   }, [error]);

   const handleChange = (
      field: keyof StelaTestEntryForm,
      value: string
   ): void => {
      setForm((currentForm) => ({ ...currentForm, [field]: value }));
      setFormError("");
   };

   const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();

      if ([form.proposalNumber, form.agentId, form.agentFirstname, form.agentLastname, form.agentIco].some((value) => !value.trim())) {
         setFormError("Vyplňte prosím všechna povinná pole.");
         return;
      }

      setRequestError("");
      void sendRequest({
         method: "POST",
          url: `${process.env.REACT_APP_BaseURL}/api/Claim/Stela2TestEntry`,
         headers: { "Content-Type": "application/json" },
         data: {
            ProposalNumber: form.proposalNumber.trim(),
            AgentId: form.agentId.trim(),
            AgentFirstname: form.agentFirstname.trim(),
            AgentLastname: form.agentLastname.trim(),
            AgentIco: form.agentIco.trim(),
            AgentPhone: form.agentPhone.trim(),
            AgentEmail: form.agentEmail.trim(),
         },
      });
   };

   return (
      <Box
         sx={{ margin: "4.5rem auto 2rem", maxWidth: "32rem", padding: "1rem" }}
      >
         <h1>Testovací vstup poradce</h1>
         <Paragraph>
            Zadejte údaje pro zahájení testovacího hlášení škody poradce.
         </Paragraph>
         <form onSubmit={handleSubmit} noValidate>
            <Box
               sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  marginTop: "1.5rem",
               }}
            >
               <CustomInput
                  labelText="Číslo návrhu"
                  value={form.proposalNumber}
                  onChange={(event) => {
                     handleChange("proposalNumber", event.target.value);
                  }}
                  validationText=""
                  placeholderText="Zadejte číslo návrhu"
               />
               <CustomInput
                  labelText="ID poradce"
                  value={form.agentId}
                  onChange={(event) => {
                     handleChange("agentId", event.target.value);
                  }}
                  validationText=""
                  placeholderText="Zadejte ID poradce"
               />
               <CustomInput
                  labelText="Jméno poradce"
                  value={form.agentFirstname}
                  onChange={(event) => {
                     handleChange("agentFirstname", event.target.value);
                  }}
                  validationText=""
                  placeholderText="Zadejte jméno poradce"
               />
               <CustomInput
                  labelText="Příjmení poradce"
                  value={form.agentLastname}
                  onChange={(event) => {
                     handleChange("agentLastname", event.target.value);
                  }}
                  validationText=""
                  placeholderText="Zadejte příjmení poradce"
               />
               <CustomInput
                  labelText="IČO poradce"
                  value={form.agentIco}
                  onChange={(event) => {
                     handleChange("agentIco", event.target.value);
                  }}
                  validationText=""
                  placeholderText="Zadejte IČO poradce"
               />
               <CustomInput
                  labelText="Telefon poradce (nepovinné)"
                  value={form.agentPhone}
                  onChange={(event) => handleChange("agentPhone", event.target.value)}
                  validationText=""
                  placeholderText="Zadejte telefon poradce"
               />
               <CustomInput
                  labelText="E-mail poradce (nepovinné)"
                  value={form.agentEmail}
                  onChange={(event) => handleChange("agentEmail", event.target.value)}
                  validationText=""
                  placeholderText="Zadejte e-mail poradce"
               />
               {(formError || requestError) && (
                  <AttentionBlock variant="Error">
                     <Paragraph>{formError || requestError}</Paragraph>
                  </AttentionBlock>
               )}
               <Box>
                  <Button type="Submit" variant="PrimaryMedium">
                     Pokračovat
                  </Button>
               </Box>
            </Box>
         </form>
      </Box>
   );
};

export default StelaTestEntryPage;
